// TaskManagementStaff.jsx - Staff Dashboard for hr_staff role
// Staff can only view and work on tasks assigned to them - no creation/assignment privileges
// Features: View tasks, add daily updates, add comments, mark tasks complete, view progress

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Grid, Paper, Typography, Button, CircularProgress, 
  useMediaQuery, IconButton, Chip, Dialog, DialogTitle, 
  DialogContent, DialogActions, TextField, MenuItem, FormControl,
  InputLabel, Select, Snackbar, Alert, Avatar, Tooltip, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Pagination, InputAdornment, Card, CardContent, Divider, Badge,
  List, ListItem, ListItemAvatar, ListItemButton, LinearProgress,
  ListItemText, SpeedDialIcon, SpeedDial, Fade, Backdrop, SpeedDialAction,
  Stepper, Step, StepLabel, StepContent
} from '@mui/material';
import {
  Dashboard as DashboardIcon, Assignment, Person, PersonAdd,
  CalendarToday, CheckCircle, Cancel, Refresh, Search, Visibility,
  Close, Add, Delete, Save, Comment, Timeline as TimelineIcon,
  Warning, Update, TaskAlt, Send, InsertComment, HowToReg,
  EventNote, TrendingUp, Notifications, DoneAll, BugReport,
  Flag, Warning as WarningIcon, WorkOutline, AccessTime,
  EmojiEvents, Star, Schedule, CheckCircleOutline
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import { format, parseISO, addDays, differenceInDays, startOfMonth, endOfMonth, isSameDay } from 'date-fns';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';

import Sidebar from '../../components/sidebars/Sidebar_staff';
import TopNav from '../../components/top_bars/TopNav_inventory';

// Styled Components
const PriorityBadge = styled(Chip)(({ theme, priority }) => ({
  backgroundColor: priority === 'high' ? theme.palette.error.main :
                  priority === 'medium' ? theme.palette.warning.main :
                  theme.palette.success.main,
  color: '#fff',
  fontWeight: 'bold',
  fontSize: '0.7rem',
  height: 24,
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const colors = {
    pending: '#ff9800',
    in_progress: '#2196f3',
    completed: '#4caf50',
    cancelled: '#9e9e9e',
    overdue: '#f44336',
    delegated: '#9c27b0',
  };
  return {
    backgroundColor: colors[status] || '#999',
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '0.7rem',
    height: 24,
  };
});

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  transition: 'transform 0.2s, box-shadow 0.2s',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const CalendarDayCell = styled(Paper)(({ theme, isCurrentMonth, hasEvents, isToday }) => ({
  minHeight: 100,
  padding: theme.spacing(1),
  backgroundColor: isCurrentMonth ? '#fff' : '#fafafa',
  border: hasEvents ? `2px solid ${theme.palette.primary.main}` : '1px solid #e0e0e0',
  position: 'relative',
  cursor: 'pointer',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'scale(1.02)',
    zIndex: 1,
  },
  ...(isToday && {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    border: `2px solid ${theme.palette.primary.main}`,
  }),
}));

const TaskManagementStaff = () => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [debugMode, setDebugMode] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  
  // State for data
  const [staffTasks, setStaffTasks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [stats, setStats] = useState({
    department_tasks: { total: 0, pending: 0, in_progress: 0, completed: 0, overdue: 0, avg_completion: 0 },
    staff_tasks: { total: 0, pending: 0, in_progress: 0, completed: 0, overdue: 0, avg_completion: 0, total_estimated_hours: 0, completed_hours: 0 },
    due_soon: 0,
    completed_this_week: 0,
  });
  const [timeline, setTimeline] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [staffPagination, setStaffPagination] = useState({ current_page: 1, per_page: 10, total: 0, total_pages: 0 });
  
  // Calendar view state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [calendarEventDialogOpen, setCalendarEventDialogOpen] = useState(false);
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);
  
  // State for current user (staff)
  const [currentUser, setCurrentUser] = useState({
    phone: '',
    name: '',
    department: '',
    email: '',
    user_type: '',
  });
  
  // State for dialogs
  const [dailyUpdateDialogOpen, setDailyUpdateDialogOpen] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);
  const [taskDetailsDialogOpen, setTaskDetailsDialogOpen] = useState(false);
  const [selectedTaskDetails, setSelectedTaskDetails] = useState(null);
  const [confirmCompleteDialogOpen, setConfirmCompleteDialogOpen] = useState(false);
  const [pendingCompleteTask, setPendingCompleteTask] = useState(null);
  
  // State for selected items
  const [selectedStaffTask, setSelectedStaffTask] = useState(null);
  
  // State for filters
  const [staffFilters, setStaffFilters] = useState({
    status: 'all',
    priority: 'all',
    search: '',
  });
  
  // State for pagination
  const [staffPage, setStaffPage] = useState(1);
  const [rowsPerPage] = useState(5);
  
  // State for forms
  const [dailyUpdateForm, setDailyUpdateForm] = useState({
    staff_task_id: '',
    update_date: format(new Date(), 'yyyy-MM-dd'),
    hours_spent: 0,
    progress_description: '',
    challenges: '',
    next_steps: '',
    status: 'on_track',
    completion_percentage: 0,
  });
  
  const [commentForm, setCommentForm] = useState({
    task_type: 'staff',
    task_id: '',
    comment: '',
  });
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // ==================== HELPER FUNCTIONS ====================
  
  const getDaysRemainingColor = (days) => {
    if (days < 0) return '#f44336';
    if (days < 2) return '#ff9800';
    if (days < 5) return '#ffc107';
    return '#4caf50';
  };
  
  const goToPreviousMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };
  
  const goToNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };
  
  const goToToday = () => {
    setCurrentMonth(new Date());
    setSelectedCalendarDate(null);
  };
  
  const handleDayClick = (date, events) => {
    setSelectedCalendarDate(date);
    setSelectedDayEvents(events);
    setCalendarEventDialogOpen(true);
  };
  
  const toggleSidebar = () => setSidebarOpen(prev => !prev);
  
  // Staff permissions - staff cannot create or assign tasks
  const canAddDailyUpdate = useCallback(() => true, []);
  const canAddComment = useCallback(() => true, []);
  const canMarkComplete = useCallback(() => true, []);
  
  const addDebug = (key, value) => {
    if (debugMode) {
      setDebugInfo(prev => ({ ...prev, [key]: value, timestamp: new Date().toISOString() }));
      console.log(`[DEBUG Staff] ${key}:`, value);
    }
  };
  
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  
  // ==================== MEMOIZED VALUES FOR CALENDAR ====================
  
  const groupedCalendarEvents = useMemo(() => {
    const groups = {};
    if (calendarEvents && calendarEvents.length > 0) {
      calendarEvents.forEach(event => {
        if (event.start) {
          const date = format(parseISO(event.start), 'yyyy-MM-dd');
          if (!groups[date]) groups[date] = [];
          groups[date].push(event);
        }
      });
    }
    return groups;
  }, [calendarEvents]);
  
  const generateCalendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const startDayOfWeek = monthStart.getDay();
    const daysFromPrevMonth = startDayOfWeek;
    const totalDaysToShow = 42;
    
    const calendarDays = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = daysFromPrevMonth; i > 0; i--) {
      const date = new Date(monthStart);
      date.setDate(monthStart.getDate() - i);
      calendarDays.push({
        date,
        isCurrentMonth: false,
        isToday: isSameDay(date, today),
        events: groupedCalendarEvents[format(date, 'yyyy-MM-dd')] || [],
      });
    }
    
    for (let d = 1; d <= monthEnd.getDate(); d++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), d);
      calendarDays.push({
        date,
        isCurrentMonth: true,
        isToday: isSameDay(date, today),
        events: groupedCalendarEvents[format(date, 'yyyy-MM-dd')] || [],
      });
    }
    
    const daysNeeded = totalDaysToShow - calendarDays.length;
    for (let i = 1; i <= daysNeeded; i++) {
      const date = new Date(monthEnd);
      date.setDate(monthEnd.getDate() + i);
      calendarDays.push({
        date,
        isCurrentMonth: false,
        isToday: isSameDay(date, today),
        events: groupedCalendarEvents[format(date, 'yyyy-MM-dd')] || [],
      });
    }
    
    return calendarDays;
  }, [currentMonth, groupedCalendarEvents]);
  
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  // ==================== DATA FETCHING FUNCTIONS ====================
  
  const fetchStaffTasks = async () => {
    if (!currentUser.phone) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('phone', currentUser.phone);
      
      if (staffFilters.status && staffFilters.status !== 'all') {
        params.append('status', staffFilters.status);
      }
      if (staffFilters.priority && staffFilters.priority !== 'all') {
        params.append('priority', staffFilters.priority);
      }
      if (staffFilters.search && staffFilters.search.trim() !== '') {
        params.append('search', staffFilters.search.trim());
      }
      
      params.append('page', staffPage);
      params.append('limit', rowsPerPage);
      
      addDebug('staff_fetch_params', Object.fromEntries(params));
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=get_staff_tasks&${params}`
      );
      
      addDebug('staff_tasks_response', { 
        success: response.data.success, 
        count: response.data.data?.tasks?.length,
        pagination: response.data.data?.pagination
      });
      
      if (response.data.success) {
        setStaffTasks(response.data.data.tasks || []);
        if (response.data.data.pagination) {
          setStaffPagination(response.data.data.pagination);
        }
      } else {
        console.error('[DEBUG Staff] Staff tasks API error:', response.data.message);
        showSnackbar(response.data.message || 'Error loading tasks', 'error');
      }
    } catch (err) {
      console.error('Error fetching staff tasks:', err);
      addDebug('fetch_staff_error', err.message);
      if (err.response) {
        console.error('[DEBUG Staff] Response status:', err.response.status);
        console.error('[DEBUG Staff] Response data:', err.response.data);
      }
      showSnackbar('Error loading your tasks. Please try refreshing.', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchNotifications = async () => {
    if (!currentUser.phone) return;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=get_notifications&phone=${currentUser.phone}`
      );
      if (response.data.success) {
        setNotifications(response.data.data);
        const unread = response.data.data.filter(n => !n.is_read).length;
        setUnreadCount(unread);
        addDebug('notifications_loaded', response.data.data.length);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      addDebug('fetch_notifications_error', err.message);
    }
  };
  
  const fetchStats = async () => {
    if (!currentUser.phone) return;
    
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=get_task_stats&phone=${currentUser.phone}`;
      addDebug('fetching_stats', url);
      
      const response = await axios.get(url);
      
      if (response.data.success && response.data.data) {
        const newStats = {
          department_tasks: {
            total: Number(response.data.data.department_tasks?.total) || 0,
            pending: Number(response.data.data.department_tasks?.pending) || 0,
            in_progress: Number(response.data.data.department_tasks?.in_progress) || 0,
            completed: Number(response.data.data.department_tasks?.completed) || 0,
            cancelled: Number(response.data.data.department_tasks?.cancelled) || 0,
            overdue: Number(response.data.data.department_tasks?.overdue) || 0,
            avg_completion: Number(response.data.data.department_tasks?.avg_completion) || 0
          },
          staff_tasks: {
            total: Number(response.data.data.staff_tasks?.total) || 0,
            pending: Number(response.data.data.staff_tasks?.pending) || 0,
            in_progress: Number(response.data.data.staff_tasks?.in_progress) || 0,
            completed: Number(response.data.data.staff_tasks?.completed) || 0,
            cancelled: Number(response.data.data.staff_tasks?.cancelled) || 0,
            overdue: Number(response.data.data.staff_tasks?.overdue) || 0,
            avg_completion: Number(response.data.data.staff_tasks?.avg_completion) || 0,
            total_estimated_hours: Number(response.data.data.staff_tasks?.total_estimated_hours) || 0,
            completed_hours: Number(response.data.data.staff_tasks?.completed_hours) || 0
          },
          due_soon: Number(response.data.data.due_soon) || 0,
          completed_this_week: Number(response.data.data.completed_this_week) || 0
        };
        
        setStats(newStats);
        addDebug('stats_updated', newStats);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      addDebug('fetch_stats_error', err.message);
    }
  };
  
  const fetchTimeline = async () => {
    if (!currentUser.phone) return;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=get_task_timeline`
      );
      if (response.data.success) {
        setTimeline(response.data.data);
        addDebug('timeline_loaded', response.data.data?.length);
      }
    } catch (err) {
      console.error('Error fetching timeline:', err);
      addDebug('fetch_timeline_error', err.message);
    }
  };
  
  const fetchCalendarEvents = async () => {
    if (!currentUser.phone) return;
    
    try {
      const startDate = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
      
      const url = `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=get_calendar_events&start=${startDate}&end=${endDate}&phone=${encodeURIComponent(currentUser.phone)}&user_type=${encodeURIComponent(currentUser.user_type || '')}`;
      
      const response = await axios.get(url);
      
      if (response.data?.success) {
        setCalendarEvents(response.data.data || []);
        addDebug('calendar_events_loaded', response.data.data?.length);
      } else {
        setCalendarEvents([]);
      }
    } catch (err) {
      console.error('Error fetching calendar events:', err);
      setCalendarEvents([]);
      addDebug('fetch_calendar_error', err.message);
    }
  };
  
  const refreshAllData = async () => {
    setRefreshKey(prev => prev + 1);
    setLoading(true);
    
    try {
      await Promise.all([
        fetchStaffTasks(),
        fetchNotifications(),
        fetchStats(),
        fetchTimeline(),
        fetchCalendarEvents(),
      ]);
      
      showSnackbar('Data refreshed successfully', 'success');
    } catch (err) {
      console.error('Error during refresh:', err);
      showSnackbar('Error refreshing data', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // ==================== ACTION FUNCTIONS ====================
  
  const updateTaskStatus = async (taskId, newStatus, completionPercentage = null) => {
    try {
      const payload = { task_id: taskId, status: newStatus };
      if (completionPercentage !== null) {
        payload.completion_percentage = completionPercentage;
      }
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=update_staff_task_status`,
        payload
      );
      
      if (response.data.success) {
        showSnackbar(`Task status updated to ${newStatus}`, 'success');
        await refreshAllData();
        return true;
      } else {
        showSnackbar(response.data.message || 'Failed to update status', 'error');
        return false;
      }
    } catch (err) {
      console.error('Error updating task status:', err);
      showSnackbar(err.response?.data?.message || 'Error updating status', 'error');
      return false;
    }
  };
  
  const handleMarkComplete = async (taskId) => {
    if (!window.confirm('Are you sure you want to mark this task as completed? This action cannot be undone.')) {
      return;
    }
    await updateTaskStatus(taskId, 'completed', 100);
  };
  
  const handleAddDailyUpdate = async () => {
    if (!dailyUpdateForm.staff_task_id) {
      showSnackbar('Task ID is missing. Please select a task first.', 'warning');
      return;
    }
    
    if (!dailyUpdateForm.progress_description || dailyUpdateForm.progress_description.trim() === '') {
      showSnackbar('Progress description is required', 'warning');
      return;
    }
    
    addDebug('submitting_daily_update', dailyUpdateForm);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=add_daily_update`,
        {
          staff_task_id: dailyUpdateForm.staff_task_id,
          phone: currentUser.phone,
          update_date: dailyUpdateForm.update_date,
          hours_spent: parseFloat(dailyUpdateForm.hours_spent) || 0,
          progress_description: dailyUpdateForm.progress_description.trim(),
          challenges: dailyUpdateForm.challenges || '',
          next_steps: dailyUpdateForm.next_steps || '',
          status: dailyUpdateForm.status,
          completion_percentage: parseInt(dailyUpdateForm.completion_percentage) || 0,
        }
      );
      
      addDebug('daily_update_response', response.data);
      
      if (response.data.success) {
        showSnackbar('Daily update added successfully', 'success');
        setDailyUpdateDialogOpen(false);
        setDailyUpdateForm({
          staff_task_id: '',
          update_date: format(new Date(), 'yyyy-MM-dd'),
          hours_spent: 0,
          progress_description: '',
          challenges: '',
          next_steps: '',
          status: 'on_track',
          completion_percentage: 0,
        });
        await refreshAllData();
      } else {
        const errorMessage = response.data.message || 'Failed to add update';
        const errors = response.data.errors || [];
        showSnackbar(errors.length > 0 ? errors[0] : errorMessage, 'error');
        addDebug('daily_update_error', response.data);
      }
    } catch (err) {
      console.error('Error adding daily update:', err);
      if (err.response?.data) {
        addDebug('daily_update_error_response', err.response.data);
        const errorMsg = err.response.data.message || err.response.data.errors?.join(', ') || 'Error adding update';
        showSnackbar(errorMsg, 'error');
      } else {
        showSnackbar('Network error. Please check your connection.', 'error');
      }
    }
  };
  
  const handleAddComment = async () => {
    if (!commentForm.comment.trim()) {
      showSnackbar('Please enter a comment', 'warning');
      return;
    }
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=add_comment`,
        {
          ...commentForm,
          phone: currentUser.phone,
          commenter_name: currentUser.name,
        }
      );
      
      if (response.data.success) {
        showSnackbar('Comment added successfully', 'success');
        setCommentDialogOpen(false);
        setCommentForm({
          task_type: 'staff',
          task_id: '',
          comment: '',
        });
        await refreshAllData();
      } else {
        showSnackbar(response.data.message || 'Failed to add comment', 'error');
      }
    } catch (err) {
      console.error('Error adding comment:', err);
      showSnackbar(err.response?.data?.message || 'Error adding comment', 'error');
    }
  };
  
  const markNotificationRead = async (notificationId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=mark_notification_read`,
        { notification_id: notificationId }
      );
      fetchNotifications();
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };
  
  const markAllNotificationsRead = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=mark_all_notifications_read`,
        { phone: currentUser.phone }
      );
      fetchNotifications();
      showSnackbar('All notifications marked as read', 'success');
    } catch (err) {
      console.error('Error marking notifications as read:', err);
    }
  };
  
  const viewTaskDetails = async (task) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/task_management.php?action=get_staff_task&id=${task.id}`
      );
      
      if (response.data.success) {
        setSelectedTaskDetails(response.data.data);
        setTaskDetailsDialogOpen(true);
        addDebug('task_details_loaded', { id: task.id });
      }
    } catch (err) {
      console.error('Error loading task details:', err);
      showSnackbar('Error loading task details', 'error');
    }
  };
  
  // ==================== LOAD USER DATA ====================
  
  const loadUserData = async () => {
    setInitialLoading(true);
    try {
      const userPhone = localStorage.getItem('phoneNumber') || 
                        localStorage.getItem('userPhone') || 
                        localStorage.getItem('phone') || 
                        '';
      const userName = localStorage.getItem('userName') || 
                       localStorage.getItem('full_name') || 
                       localStorage.getItem('name') || 
                       'Staff User';
      const userDept = localStorage.getItem('userDepartment') || 
                       localStorage.getItem('department') || 
                       '';
      const userEmail = localStorage.getItem('email') || 
                        localStorage.getItem('userEmail') || 
                        '';
      const userType = localStorage.getItem('role') || 
                       localStorage.getItem('user_type') || 
                       'hr_staff';
      
      console.log('[DEBUG Staff] Loading user data:', { userPhone, userName, userDept, userEmail, userType });
      addDebug('user_data', { userPhone, userName, userDept, userEmail, userType });
      
      setCurrentUser({
        phone: userPhone || '',
        name: userName,
        department: userDept,
        email: userEmail,
        user_type: userType,
      });
      
      await refreshAllData();
    } catch (error) {
      console.error('Error loading user data:', error);
      addDebug('load_user_error', error.message);
      showSnackbar('Error loading user data', 'error');
    } finally {
      setInitialLoading(false);
    }
  };
  
  // ==================== EFFECTS ====================
  
  useEffect(() => {
    loadUserData();
  }, []);
  
  useEffect(() => {
    if (!initialLoading && currentUser.phone) {
      const debounceTimer = setTimeout(() => {
        fetchStaffTasks();
      }, 500);
      return () => clearTimeout(debounceTimer);
    }
  }, [staffFilters, staffPage, currentUser.phone, initialLoading]);
  
  useEffect(() => {
    if (!initialLoading && currentUser.phone) {
      fetchStats();
    }
  }, [refreshKey, currentUser.phone, initialLoading]);
  
  useEffect(() => {
    if (!initialLoading && currentUser.phone) {
      fetchCalendarEvents();
    }
  }, [currentMonth, currentUser.phone, initialLoading]);
  
  // ==================== RENDER FUNCTIONS ====================
  
  const renderStatCards = () => {
    const statItems = [
      {
        title: 'My Tasks',
        value: stats.staff_tasks?.total || 0,
        icon: <Assignment sx={{ fontSize: 40 }} />,
        subtext: `${stats.staff_tasks?.in_progress || 0} in progress`,
        bgGradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      },
      {
        title: 'Completed',
        value: stats.staff_tasks?.completed || 0,
        icon: <CheckCircle sx={{ fontSize: 40 }} />,
        subtext: `${stats.completed_this_week || 0} this week`,
        bgGradient: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
      },
      {
        title: 'Pending',
        value: stats.staff_tasks?.pending || 0,
        icon: <Schedule sx={{ fontSize: 40 }} />,
        subtext: 'Awaiting action',
        bgGradient: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
      },
      {
        title: 'Due Soon',
        value: stats.due_soon || 0,
        icon: <AccessTime sx={{ fontSize: 40 }} />,
        subtext: 'Due in 7 days',
        bgGradient: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
      },
      {
        title: 'Overdue',
        value: stats.staff_tasks?.overdue || 0,
        icon: <WarningIcon sx={{ fontSize: 40 }} />,
        subtext: 'Past deadline',
        bgGradient: 'linear-gradient(135deg, #f44336 0%, #ff7961 100%)',
      },
      {
        title: 'Completion Rate',
        value: `${Math.round(stats.staff_tasks?.avg_completion || 0)}%`,
        icon: <EmojiEvents sx={{ fontSize: 40 }} />,
        subtext: 'Overall progress',
        bgGradient: 'linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)',
      },
    ];
    
    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statItems.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
            <StyledCard sx={{ background: stat.bgGradient, color: '#fff' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="h3" fontWeight="bold">{stat.value}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>{stat.title}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
                      {stat.subtext}
                    </Typography>
                  </Box>
                  <Box sx={{ opacity: 0.8 }}>{stat.icon}</Box>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        ))}
      </Grid>
    );
  };
  
  const renderMyTasksTable = () => {
    return (
      <Paper elevation={0} sx={{ p: 3, borderRadius: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Typography variant="h5" fontWeight="bold">
            <TaskAlt sx={{ mr: 1, verticalAlign: 'middle' }} />
            My Assigned Tasks
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search tasks..."
              value={staffFilters.search}
              onChange={(e) => {
                setStaffFilters({ ...staffFilters, search: e.target.value });
                setStaffPage(1);
              }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select 
                value={staffFilters.status} 
                onChange={(e) => {
                  setStaffFilters({ ...staffFilters, status: e.target.value });
                  setStaffPage(1);
                }} 
                displayEmpty
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select 
                value={staffFilters.priority} 
                onChange={(e) => {
                  setStaffFilters({ ...staffFilters, priority: e.target.value });
                  setStaffPage(1);
                }} 
                displayEmpty
              >
                <MenuItem value="all">All Priorities</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" startIcon={<Refresh />} onClick={refreshAllData}>Refresh</Button>
          </Box>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><strong>Task Title</strong></TableCell>
                <TableCell><strong>Department</strong></TableCell>
                <TableCell><strong>Assigned By</strong></TableCell>
                <TableCell><strong>Priority</strong></TableCell>
                <TableCell><strong>Deadline</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Progress</strong></TableCell>
                <TableCell><strong>Last Update</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 8 }}><CircularProgress /></TableCell></TableRow>
              ) : staffTasks.length === 0 ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                  <Typography color="textSecondary">No tasks assigned to you yet.</Typography>
                  <Typography variant="caption" color="textSecondary">When tasks are assigned, they will appear here.</Typography>
                </TableCell></TableRow>
              ) : (
                staffTasks.map((task) => {
                  const isOverdue = new Date(task.deadline) < new Date() && task.status !== 'completed';
                  const daysRemaining = differenceInDays(parseISO(task.deadline), new Date());
                  const isCompleted = task.status === 'completed';
                  const isInProgress = task.status === 'in_progress';
                  
                  return (
                    <TableRow key={task.id} hover sx={{ 
                      bgcolor: isOverdue ? '#ffebee' : 
                               isCompleted ? alpha('#4caf50', 0.05) : 
                               isInProgress ? alpha('#2196f3', 0.03) : 'inherit' 
                    }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">{task.task_title}</Typography>
                        {task.task_description && (
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                            {task.task_description.substring(0, 60)}...
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{task.department_name || task.department || 'N/A'}</TableCell>
                      <TableCell>
                        <Tooltip title={`Assigned by: ${task.assigned_by || 'Unknown'}`}>
                          <Chip size="small" icon={<Person fontSize="small" />} label={task.assigned_by || 'N/A'} variant="outlined" />
                        </Tooltip>
                      </TableCell>
                      <TableCell><PriorityBadge label={task.priority.toUpperCase()} priority={task.priority} size="small" /></TableCell>
                      <TableCell>
                        <Typography variant="body2" color={isOverdue ? 'error' : 'textPrimary'}>
                          {format(parseISO(task.deadline), 'MMM dd, yyyy HH:mm')}
                        </Typography>
                        {!isOverdue && daysRemaining > 0 && (
                          <Typography variant="caption" sx={{ color: getDaysRemainingColor(daysRemaining) }}>
                            {daysRemaining} days left
                          </Typography>
                        )}
                        {isOverdue && <Typography variant="caption" color="error">Overdue</Typography>}
                      </TableCell>
                      <TableCell><StatusChip label={task.status.replace('_', ' ').toUpperCase()} status={task.status} size="small" /></TableCell>
                      <TableCell>
                        <Box sx={{ minWidth: 100 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={task.completion_percentage || 0} 
                            sx={{ height: 6, borderRadius: 3, mb: 0.5 }} 
                          />
                          <Typography variant="caption" color="textSecondary">{task.completion_percentage || 0}%</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {task.last_update_at ? (
                          <Typography variant="caption">
                            {format(parseISO(task.last_update_at), 'MMM dd')}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="textSecondary">No updates</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => viewTaskDetails(task)} title="View Details">
                          <Visibility fontSize="small" />
                        </IconButton>
                        {task.status !== 'completed' && task.status !== 'cancelled' && (
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={() => {
                              setSelectedStaffTask(task);
                              setDailyUpdateForm({ 
                                ...dailyUpdateForm, 
                                staff_task_id: task.id, 
                                completion_percentage: task.completion_percentage || 0 
                              });
                              setDailyUpdateDialogOpen(true);
                            }} 
                            title="Add Daily Update"
                          >
                            <Update fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton size="small" onClick={() => {
                          setCommentForm({ ...commentForm, task_id: task.id, task_type: 'staff' });
                          setCommentDialogOpen(true);
                        }} title="Add Comment">
                          <Comment fontSize="small" />
                        </IconButton>
                        {task.status !== 'completed' && task.status !== 'cancelled' && (
                          <IconButton 
                            size="small" 
                            color="success" 
                            onClick={() => handleMarkComplete(task.id)} 
                            title="Mark Complete"
                          >
                            <CheckCircle fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {staffPagination.total_pages > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination 
              count={staffPagination.total_pages} 
              page={staffPage} 
              onChange={(e, value) => setStaffPage(value)} 
              color="primary" 
            />
          </Box>
        )}
      </Paper>
    );
  };
  
  const renderAnalyticsCharts = () => {
    const COLORS = ['#4caf50', '#ff9800', '#2196f3', '#f44336', '#9e9e9e'];
    
    const pieData = [
      { name: 'Completed', value: stats.staff_tasks?.completed || 0 },
      { name: 'In Progress', value: stats.staff_tasks?.in_progress || 0 },
      { name: 'Pending', value: stats.staff_tasks?.pending || 0 },
      { name: 'Overdue', value: stats.staff_tasks?.overdue || 0 },
      { name: 'Cancelled', value: stats.staff_tasks?.cancelled || 0 },
    ].filter(d => d.value > 0);
    
    // Prepare weekly progress data from tasks
    const weeklyProgress = staffTasks.map(task => ({
      name: task.task_title.length > 20 ? task.task_title.substring(0, 20) + '...' : task.task_title,
      progress: task.completion_percentage || 0,
      priority: task.priority,
      deadline: task.deadline
    })).slice(0, 10);
    
    return (
      <Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, borderRadius: 4 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                <TimelineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                My Task Progress
              </Typography>
              {weeklyProgress.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={weeklyProgress} margin={{ top: 20, right: 30, left: 20, bottom: 80 }} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 100]} />
                    <YAxis type="category" dataKey="name" width={150} />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="progress" fill="#8884d8" name="Completion %" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography color="textSecondary" align="center" py={4}>No task progress data available</Typography>
              )}
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: 4 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>My Task Distribution</Typography>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Typography color="textSecondary" align="center" py={4}>No task data available</Typography>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              <Box>
                <Typography variant="subtitle2" gutterBottom>My Performance Metrics</Typography>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="textSecondary">Overall Completion Rate</Typography>
                  <Typography variant="h6" color="success.main">{Math.round(stats.staff_tasks?.avg_completion || 0)}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={stats.staff_tasks?.avg_completion || 0} sx={{ height: 8, borderRadius: 4, mb: 2 }} />
                
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="textSecondary">Productivity</Typography>
                  <Typography variant="h6">{stats.staff_tasks?.completed_hours || 0}/{stats.staff_tasks?.total_estimated_hours || 0} hrs</Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={stats.staff_tasks?.total_estimated_hours > 0 ? (stats.staff_tasks?.completed_hours || 0) / (stats.staff_tasks?.total_estimated_hours || 1) * 100 : 0} 
                  sx={{ height: 8, borderRadius: 4 }} 
                />
                
                <Box display="flex" justifyContent="space-between" mt={2} mb={1}>
                  <Typography variant="body2" color="textSecondary">Tasks Completed This Week</Typography>
                  <Typography variant="h6" color="info.main">{stats.completed_this_week || 0}</Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="textSecondary">On-Time Completion Rate</Typography>
                  <Typography variant="h6" color="warning.main">
                    {stats.staff_tasks?.completed > 0 
                      ? Math.round((stats.staff_tasks?.completed - (stats.staff_tasks?.overdue || 0)) / stats.staff_tasks?.completed * 100) 
                      : 0}%
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 4 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
                Department Performance Overview
              </Typography>
              {timeline.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={timeline} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month_label" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Area type="monotone" dataKey="tasks_created" stackId="1" stroke="#8884d8" fill="#8884d8" name="Tasks Created" />
                    <Area type="monotone" dataKey="tasks_completed" stackId="2" stroke="#82ca9d" fill="#82ca9d" name="Tasks Completed" />
                    <Area type="monotone" dataKey="staff_tasks_completed" stackId="3" stroke="#ffc658" fill="#ffc658" name="Staff Tasks Completed" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Typography color="textSecondary" align="center" py={4}>No timeline data available</Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };
  
  const renderCalendarView = () => {
    const hasEvents = calendarEvents.length > 0;
    
    return (
      <Paper sx={{ p: 3, borderRadius: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Typography variant="h5" fontWeight="bold">
            <CalendarToday sx={{ mr: 1, verticalAlign: 'middle' }} />
            My Task Calendar
          </Typography>
          <Box display="flex" gap={1}>
            <Button size="small" onClick={goToPreviousMonth} variant="outlined">Previous</Button>
            <Button size="small" onClick={goToToday} variant={currentMonth.toDateString() === new Date().toDateString() ? 'contained' : 'outlined'}>Today</Button>
            <Button size="small" onClick={goToNextMonth} variant="outlined">Next</Button>
          </Box>
        </Box>
        
        <Typography variant="h6" align="center" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
          {format(currentMonth, 'MMMM yyyy')}
        </Typography>
        
        <Grid container spacing={1} sx={{ mb: 1 }}>
          {weekDays.map(day => (
            <Grid item xs key={day}>
              <Typography align="center" variant="subtitle2" fontWeight="bold" sx={{ py: 1, bgcolor: 'primary.main', color: 'white', borderRadius: 1, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                {day}
              </Typography>
            </Grid>
          ))}
        </Grid>
        
        <Grid container spacing={1}>
          {generateCalendarDays.map((day, index) => {
            const hasEventsOnDay = day.events.length > 0;
            const isToday = day.isToday;
            const isCurrentMonth = day.isCurrentMonth;
            
            return (
              <Grid item xs={12/7} key={index}>
                <CalendarDayCell 
                  isCurrentMonth={isCurrentMonth} 
                  hasEvents={hasEventsOnDay} 
                  isToday={isToday} 
                  onClick={() => hasEventsOnDay && handleDayClick(day.date, day.events)}
                >
                  <Box textAlign="center">
                    <Typography variant="body2" fontWeight={isCurrentMonth ? 'normal' : 'light'} sx={{ color: isCurrentMonth ? 'text.primary' : 'text.disabled', fontWeight: isToday ? 'bold' : 'normal' }}>
                      {format(day.date, 'd')}
                    </Typography>
                  </Box>
                  {hasEventsOnDay && (
                    <Box sx={{ mt: 1 }}>
                      {day.events.slice(0, 2).map((event, idx) => (
                        <Tooltip key={idx} title={event.extendedProps?.full_title || event.title}>
                          <Chip 
                            size="small" 
                            label={event.title.length > 15 ? event.title.substring(0, 12) + '...' : event.title} 
                            sx={{ width: '100%', mb: 0.5, fontSize: '0.65rem', height: 20, bgcolor: event.color || '#757575', color: '#fff', '& .MuiChip-label': { px: 0.5 } }} 
                          />
                        </Tooltip>
                      ))}
                      {day.events.length > 2 && (
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem', display: 'block', textAlign: 'center' }}>
                          +{day.events.length - 2} more
                        </Typography>
                      )}
                    </Box>
                  )}
                </CalendarDayCell>
              </Grid>
            );
          })}
        </Grid>
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Box display="flex" alignItems="center" gap={1}><Box sx={{ width: 12, height: 12, bgcolor: '#4caf50', borderRadius: 1 }} /><Typography variant="caption">Completed</Typography></Box>
          <Box display="flex" alignItems="center" gap={1}><Box sx={{ width: 12, height: 12, bgcolor: '#ff9800', borderRadius: 1 }} /><Typography variant="caption">Pending</Typography></Box>
          <Box display="flex" alignItems="center" gap={1}><Box sx={{ width: 12, height: 12, bgcolor: '#2196f3', borderRadius: 1 }} /><Typography variant="caption">In Progress</Typography></Box>
          <Box display="flex" alignItems="center" gap={1}><Box sx={{ width: 12, height: 12, bgcolor: '#f44336', borderRadius: 1 }} /><Typography variant="caption">Overdue</Typography></Box>
          <Box display="flex" alignItems="center" gap={1}><Box sx={{ width: 12, height: 12, bgcolor: '#e0e0e0', border: '2px solid #2196f3' }} /><Typography variant="caption">Today</Typography></Box>
        </Box>
        
        {!hasEvents && (
          <Typography color="textSecondary" align="center" sx={{ mt: 4, py: 4 }}>
            <EventNote sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <br />
            No tasks scheduled for {format(currentMonth, 'MMMM yyyy')}
          </Typography>
        )}
      </Paper>
    );
  };
  
  // Dialog render functions
  const renderCalendarEventDialog = () => (
    <Dialog open={calendarEventDialogOpen} onClose={() => setCalendarEventDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="bold">
            <EventNote sx={{ mr: 1, verticalAlign: 'middle' }} />
            Tasks for {selectedCalendarDate ? format(selectedCalendarDate, 'MMMM dd, yyyy') : ''}
          </Typography>
          <IconButton onClick={() => setCalendarEventDialogOpen(false)}><Close /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {selectedDayEvents.length === 0 ? (
          <Typography color="textSecondary" align="center" py={4}>No tasks scheduled for this day</Typography>
        ) : (
          <List>
            {selectedDayEvents.map((event, index) => (
              <ListItem key={index} divider>
                <ListItemAvatar><Avatar sx={{ bgcolor: event.color || '#757575' }}><TaskAlt /></Avatar></ListItemAvatar>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography variant="subtitle1" fontWeight="bold">{event.extendedProps?.full_title || event.title}</Typography>
                      <PriorityBadge label={event.extendedProps?.priority_text || 'MEDIUM'} priority={event.extendedProps?.priority || 'medium'} size="small" />
                      <StatusChip label={event.extendedProps?.status?.replace('_', ' ').toUpperCase() || 'PENDING'} status={event.extendedProps?.status || 'pending'} size="small" />
                    </Box>
                  }
                  secondary={
                    <Box mt={1}>
                      <Typography variant="body2" color="textSecondary"><strong>Assigned By:</strong> {event.extendedProps?.assigned_by || 'N/A'}</Typography>
                      <Typography variant="body2" color="textSecondary"><strong>Completion:</strong> {event.extendedProps?.completion_percentage || 0}%</Typography>
                      <LinearProgress variant="determinate" value={event.extendedProps?.completion_percentage || 0} sx={{ height: 4, borderRadius: 2, mt: 1, width: '60%' }} />
                    </Box>
                  }
                />
                <Button variant="outlined" size="small" onClick={() => { setCalendarEventDialogOpen(false); viewTaskDetails({ id: event.id }); }}>View Details</Button>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions><Button onClick={() => setCalendarEventDialogOpen(false)}>Close</Button></DialogActions>
    </Dialog>
  );
  
  const renderDailyUpdateDialog = () => (
    <Dialog open={dailyUpdateDialogOpen} onClose={() => setDailyUpdateDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          <Update sx={{ mr: 1, verticalAlign: 'middle' }} />
          Add Daily Update
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Task: <strong>{selectedStaffTask?.task_title || 'Selected Task'}</strong>
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField 
              label="Update Date" 
              type="date" 
              fullWidth 
              InputLabelProps={{ shrink: true }} 
              value={dailyUpdateForm.update_date} 
              onChange={(e) => setDailyUpdateForm({ ...dailyUpdateForm, update_date: e.target.value })} 
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField 
              label="Hours Spent" 
              type="number" 
              fullWidth 
              InputProps={{ inputProps: { min: 0, max: 24, step: 0.5 } }} 
              value={dailyUpdateForm.hours_spent} 
              onChange={(e) => setDailyUpdateForm({ ...dailyUpdateForm, hours_spent: parseFloat(e.target.value) || 0 })} 
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField 
              label="Completion Percentage" 
              type="number" 
              fullWidth 
              InputProps={{ inputProps: { min: 0, max: 100 } }} 
              value={dailyUpdateForm.completion_percentage} 
              onChange={(e) => setDailyUpdateForm({ ...dailyUpdateForm, completion_percentage: parseInt(e.target.value) || 0 })} 
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select 
                value={dailyUpdateForm.status} 
                onChange={(e) => setDailyUpdateForm({ ...dailyUpdateForm, status: e.target.value })} 
                label="Status"
              >
                <MenuItem value="on_track">On Track</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField 
              label="Progress Description *" 
              fullWidth 
              multiline 
              rows={3} 
              required 
              value={dailyUpdateForm.progress_description} 
              onChange={(e) => setDailyUpdateForm({ ...dailyUpdateForm, progress_description: e.target.value })} 
              placeholder="What did you accomplish today?"
              error={dailyUpdateForm.progress_description === ''}
              helperText={dailyUpdateForm.progress_description === '' ? 'Progress description is required' : ''}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField 
              label="Challenges Faced" 
              fullWidth 
              multiline 
              rows={2} 
              value={dailyUpdateForm.challenges} 
              onChange={(e) => setDailyUpdateForm({ ...dailyUpdateForm, challenges: e.target.value })} 
              placeholder="Any blockers or difficulties?" 
            />
          </Grid>
          <Grid item xs={12}>
            <TextField 
              label="Next Steps" 
              fullWidth 
              multiline 
              rows={2} 
              value={dailyUpdateForm.next_steps} 
              onChange={(e) => setDailyUpdateForm({ ...dailyUpdateForm, next_steps: e.target.value })} 
              placeholder="What are your plans for tomorrow?" 
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDailyUpdateDialogOpen(false)}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleAddDailyUpdate} 
          startIcon={<Send />}
          disabled={!dailyUpdateForm.progress_description || dailyUpdateForm.progress_description.trim() === ''}
        >
          Submit Update
        </Button>
      </DialogActions>
    </Dialog>
  );
  
  const renderCommentDialog = () => (
    <Dialog open={commentDialogOpen} onClose={() => setCommentDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          <InsertComment sx={{ mr: 1, verticalAlign: 'middle' }} />
          Add Comment
        </Typography>
      </DialogTitle>
      <DialogContent>
        <TextField 
          label="Comment" 
          fullWidth 
          multiline 
          rows={4} 
          margin="normal" 
          value={commentForm.comment} 
          onChange={(e) => setCommentForm({ ...commentForm, comment: e.target.value })} 
          placeholder="Write your comment here..." 
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCommentDialogOpen(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleAddComment} startIcon={<InsertComment />}>Add Comment</Button>
      </DialogActions>
    </Dialog>
  );
  
  const renderNotificationsDrawer = () => (
    <Dialog open={notificationDrawerOpen} onClose={() => setNotificationDrawerOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="bold">
            <Notifications sx={{ mr: 1, verticalAlign: 'middle' }} />
            Notifications
            {unreadCount > 0 && <Badge badgeContent={unreadCount} color="error" sx={{ ml: 1 }} />}
          </Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={markAllNotificationsRead} startIcon={<DoneAll />}>Mark all as read</Button>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {notifications.length === 0 ? (
          <Typography color="textSecondary" align="center" py={4}>No notifications</Typography>
        ) : (
          notifications.map(notification => (
            <Card 
              key={notification.id} 
              sx={{ 
                mb: 2, 
                p: 2, 
                bgcolor: notification.is_read ? 'transparent' : alpha('#2196f3', 0.1), 
                cursor: 'pointer', 
                transition: 'all 0.2s', 
                '&:hover': { bgcolor: alpha('#2196f3', 0.05) } 
              }} 
              onClick={() => markNotificationRead(notification.id)}
            >
              <Box display="flex" gap={2}>
                <Box>
                  {notification.notification_type === 'assigned' && <Assignment color="primary" />}
                  {notification.notification_type === 'updated' && <Update color="info" />}
                  {notification.notification_type === 'completed' && <CheckCircle color="success" />}
                  {notification.notification_type === 'commented' && <Comment color="warning" />}
                </Box>
                <Box flex={1}>
                  <Typography variant="body2" fontWeight="bold">{notification.title}</Typography>
                  <Typography variant="caption" color="textSecondary" display="block">{notification.message}</Typography>
                  <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                    {format(parseISO(notification.created_at), 'MMM dd, yyyy HH:mm')}
                  </Typography>
                </Box>
                {!notification.is_read && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2196f3' }} />}
              </Box>
            </Card>
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setNotificationDrawerOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );
  
  const renderTaskDetailsDialog = () => {
    if (!selectedTaskDetails) return null;
    
    return (
      <Dialog open={taskDetailsDialogOpen} onClose={() => setTaskDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="bold">Task Details</Typography>
            <IconButton onClick={() => setTaskDetailsDialogOpen(false)}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Paper sx={{ p: 3, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>{selectedTaskDetails.task_title}</Typography>
                <Typography variant="body2" color="textSecondary">{selectedTaskDetails.task_description || 'No description provided'}</Typography>
                <Box mt={2} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <Chip size="small" icon={<Person />} label={`Assigned by: ${selectedTaskDetails.assigned_by || 'System'}`} variant="outlined" />
                  <PriorityBadge label={selectedTaskDetails.priority?.toUpperCase()} priority={selectedTaskDetails.priority} size="small" />
                  <StatusChip label={selectedTaskDetails.status?.replace('_', ' ').toUpperCase()} status={selectedTaskDetails.status} size="small" />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>Task Information</Typography>
                <Typography variant="body2" mb={1}><strong>Completion:</strong> {selectedTaskDetails.completion_percentage || 0}%</Typography>
                <Typography variant="body2" mb={1}><strong>Staff:</strong> {selectedTaskDetails.staff_name}</Typography>
                <Typography variant="body2" mb={1}><strong>Phone:</strong> {selectedTaskDetails.phone}</Typography>
                <Typography variant="body2" mb={1}><strong>Estimated Hours:</strong> {selectedTaskDetails.estimated_hours || 0}</Typography>
                {selectedTaskDetails.department_name && (
                  <Typography variant="body2" mb={1}><strong>Department:</strong> {selectedTaskDetails.department_name}</Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>Dates</Typography>
                <Typography variant="body2" mb={1}><strong>Scheduled Date:</strong> {format(parseISO(selectedTaskDetails.scheduled_date), 'MMMM dd, yyyy')}</Typography>
                <Typography variant="body2" mb={1}><strong>Deadline:</strong> {format(parseISO(selectedTaskDetails.deadline), 'MMMM dd, yyyy HH:mm')}</Typography>
                <Typography variant="body2" mb={1}><strong>Created:</strong> {format(parseISO(selectedTaskDetails.created_at), 'MMM dd, yyyy')}</Typography>
                {selectedTaskDetails.completed_at && (
                  <Typography variant="body2" mb={1}><strong>Completed:</strong> {format(parseISO(selectedTaskDetails.completed_at), 'MMM dd, yyyy HH:mm')}</Typography>
                )}
              </Paper>
            </Grid>
            {selectedTaskDetails.daily_updates && selectedTaskDetails.daily_updates.length > 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Update History</Typography>
                  <List dense>
                    {selectedTaskDetails.daily_updates.map((update, idx) => (
                      <ListItem key={idx} divider>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                              <Typography variant="body2" fontWeight="bold">{format(parseISO(update.update_date), 'MMM dd, yyyy')}</Typography>
                              <Chip size="small" label={`${update.hours_spent || 0} hrs`} variant="outlined" />
                              <Chip size="small" label={update.status} variant="outlined" color={update.status === 'completed' ? 'success' : update.status === 'blocked' ? 'error' : 'info'} />
                            </Box>
                          }
                          secondary={
                            <Box mt={1}>
                              <Typography variant="body2"><strong>Progress:</strong> {update.progress_description}</Typography>
                              {update.challenges && <Typography variant="body2" color="textSecondary"><strong>Challenges:</strong> {update.challenges}</Typography>}
                              {update.next_steps && <Typography variant="body2" color="textSecondary"><strong>Next Steps:</strong> {update.next_steps}</Typography>}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            )}
            {selectedTaskDetails.comments && selectedTaskDetails.comments.length > 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Comments</Typography>
                  <List dense>
                    {selectedTaskDetails.comments.map((comment, idx) => (
                      <ListItem key={idx} divider>
                        <ListItemAvatar>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                            {comment.commenter_name?.charAt(0) || 'U'}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                              <Typography variant="body2" fontWeight="bold">{comment.commenter_name}</Typography>
                              <Typography variant="caption" color="textSecondary">
                                {format(parseISO(comment.created_at), 'MMM dd, yyyy HH:mm')}
                              </Typography>
                            </Box>
                          }
                          secondary={comment.comment}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          {selectedTaskDetails.status !== 'completed' && selectedTaskDetails.status !== 'cancelled' && (
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => { 
                setTaskDetailsDialogOpen(false); 
                setDailyUpdateForm({ 
                  ...dailyUpdateForm, 
                  staff_task_id: selectedTaskDetails.id, 
                  completion_percentage: selectedTaskDetails.completion_percentage || 0 
                }); 
                setDailyUpdateDialogOpen(true); 
              }} 
              startIcon={<Update />}
            >
              Add Daily Update
            </Button>
          )}
          <Button onClick={() => setTaskDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };
  
  // ==================== MAIN RENDER ====================
  
  if (initialLoading) {
    return (
      <Backdrop open={true} sx={{ zIndex: 9999 }}>
        <Box textAlign="center">
          <CircularProgress color="primary" />
          <Typography sx={{ mt: 2 }}>Loading Staff Dashboard...</Typography>
        </Box>
      </Backdrop>
    );
  }
  
  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f8f9fa' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <Box sx={{ flexGrow: 1, p: 3, ml: isMobile ? 0 : sidebarOpen ? 30 : 5, maxWidth: '1600px', mx: 'auto', width: '100%', overflow: 'auto' }}>
        <TopNav 
          onLogout={() => { 
            localStorage.clear(); 
            window.location.href = '/login'; 
          }} 
          onNotificationClick={() => setNotificationDrawerOpen(true)} 
          toggleSidebar={toggleSidebar} 
          notificationCount={unreadCount} 
        />
        
        {debugMode && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: '#263238', color: '#fff', fontSize: '0.75rem', overflow: 'auto', maxHeight: 200 }}>
            <Typography variant="caption" component="pre">{JSON.stringify(debugInfo, null, 2)}</Typography>
          </Paper>
        )}
        
        <Fade in timeout={500}>
          <Box sx={{ mt: 4 }}>
            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
              <Tab icon={<DashboardIcon />} label="Dashboard" />
              <Tab icon={<TaskAlt />} label="My Tasks" />
              <Tab icon={<TimelineIcon />} label="Analytics" />
              <Tab icon={<CalendarToday />} label="Calendar" />
            </Tabs>
            
            {activeTab === 0 && (
              <Box>
                {renderStatCards()}
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3, borderRadius: 4 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        <WorkOutline sx={{ mr: 1, verticalAlign: 'middle' }} />
                        My Recent Tasks
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Task</strong></TableCell>
                              <TableCell><strong>Department</strong></TableCell>
                              <TableCell><strong>Priority</strong></TableCell>
                              <TableCell><strong>Deadline</strong></TableCell>
                              <TableCell><strong>Status</strong></TableCell>
                              <TableCell><strong>Progress</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {staffTasks.slice(0, 5).map((task) => {
                              const isOverdue = new Date(task.deadline) < new Date() && task.status !== 'completed';
                              return (
                                <TableRow key={task.id} hover>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="bold">{task.task_title}</Typography>
                                  </TableCell>
                                  <TableCell>{task.department_name || task.department || 'N/A'}</TableCell>
                                  <TableCell><PriorityBadge label={task.priority.toUpperCase()} priority={task.priority} size="small" /></TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color={isOverdue ? 'error' : 'textPrimary'}>
                                      {format(parseISO(task.deadline), 'MMM dd, yyyy')}
                                    </Typography>
                                  </TableCell>
                                  <TableCell><StatusChip label={task.status.replace('_', ' ').toUpperCase()} status={task.status} size="small" /></TableCell>
                                  <TableCell>
                                    <LinearProgress 
                                      variant="determinate" 
                                      value={task.completion_percentage || 0} 
                                      sx={{ height: 6, borderRadius: 2, width: 100 }} 
                                    />
                                    <Typography variant="caption">{task.completion_percentage || 0}%</Typography>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            {staffTasks.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={6} align="center">
                                  <Typography color="textSecondary">No tasks assigned yet</Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
            
            {activeTab === 1 && renderMyTasksTable()}
            {activeTab === 2 && renderAnalyticsCharts()}
            {activeTab === 3 && renderCalendarView()}
          </Box>
        </Fade>
      </Box>
      
      <SpeedDial 
        ariaLabel="Quick Actions" 
        sx={{ position: 'fixed', bottom: 16, right: 16 }} 
        icon={<SpeedDialIcon />}
      >
        <SpeedDialAction 
          icon={<TaskAlt />} 
          tooltipTitle="Add Daily Update" 
          onClick={() => { 
            const activeTask = staffTasks.find(t => t.status !== 'completed');
            if (activeTask) { 
              setSelectedStaffTask(activeTask);
              setDailyUpdateForm({ 
                ...dailyUpdateForm, 
                staff_task_id: activeTask.id, 
                completion_percentage: activeTask.completion_percentage || 0 
              });
              setDailyUpdateDialogOpen(true);
            } else { 
              showSnackbar('No active tasks to update', 'warning'); 
            } 
          }} 
        />
        <SpeedDialAction icon={<Refresh />} tooltipTitle="Refresh All Data" onClick={refreshAllData} />
        <SpeedDialAction 
          icon={debugMode ? <CheckCircle /> : <BugReport />} 
          tooltipTitle={debugMode ? "Disable Debug" : "Enable Debug"} 
          onClick={() => setDebugMode(!debugMode)} 
        />
      </SpeedDial>
      
      {renderDailyUpdateDialog()}
      {renderCommentDialog()}
      {renderTaskDetailsDialog()}
      {renderNotificationsDrawer()}
      {renderCalendarEventDialog()}
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TaskManagementStaff;